<script setup>
import {useRouter} from "vue-router";
import {computed, onMounted, ref, nextTick} from "vue";
import axios from "@/axios";
import LogoComponent from "@/components/top/logoComponent.vue";
import {useComStore, useLanguageStore} from "@/pinia";

const committeesRef = ref(null);
const canScrollUp = ref(false);
const canScrollDown = ref(false);

const checkScroll = () => {
  const el = committeesRef.value;
  if (!el) return;
  canScrollUp.value = el.scrollTop > 0;
  canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
};

const scrollCommittees = (direction) => {
  const el = committeesRef.value;
  if (!el) return;
  const rowHeight = el.querySelector('.committee')?.offsetHeight || 300;
  el.scrollBy({ top: direction === 'down' ? rowHeight + 10 : -(rowHeight + 10), behavior: 'smooth' });
};

const languageStore = useLanguageStore();
const liveList = ref(null);
const router = useRouter();
const codes = ref(null);
const committee = ref([]);
const plenary = ref();
const useComCode = useComStore();
const isLoading = ref(true);

const computedCodes = computed(() => {
  const committee = liveList.value?.filter(live => live.committeeId) || [];
  const committeeIds = committee.map(committee => committee?.committeeId?.codeId)
  return (codes.value || []).filter(code => !committeeIds.includes(code.codeId));
})

const clickVideo = (e) => {
  const url = 'https://youtu.be/' + e;
  window.open(url);
}

const clickedVideoConference = (committeeId) => {
  router.push({
    name: 'recordVideo',
    params: {
      committeeId: committeeId.committeeId
    }
  })
}
const getMeetingData = () => {
  isLoading.value = true;
  axios.get("/api/meeting/live")
      .then(s => {
        liveList.value = s.data;
        s.data.forEach((e) => {
          if (e.meetingType.codeId === 'mt002') plenary.value = e
          else {
            committee.value = [...committee.value, e]
          }
        })

        nextTick(() => checkScroll());
      }).catch(e => {
    console.log(e);
  }).finally(() => {
    isLoading.value = false;
  })
}


onMounted(() => {
  codes.value = useComCode.getCodeType('committee')
  getMeetingData()

})
</script>

<template>
  <div class="liveVideoArea">
    <div v-if="isLoading" class="loadingArea">
      <v-progress-circular indeterminate color="white" size="48"></v-progress-circular>
    </div>
    <div v-else class="liveVideos">
      <div class="ml005">
        <template v-if="plenary != null">
          <div class="d-flex justify-content-between liveVideo" @click="clickVideo(plenary.liveId)">
            <div class="title">{{ plenary.meetingType.codeNm }}</div>
            <div class="d-flex">
              <div><img src="../../../assets/youtubeLogo.png" class="ylogo"/></div>
              <div class="liveText">live</div>
            </div>
          </div>
          <div class="thumbnail" @click="clickVideo(plenary.liveId)">
            <template v-if="plenary.thumbnailUrl != null ">
              <v-img :src="plenary.thumbnailUrl"></v-img>
            </template>
            <template v-else>
              <div class="notThumbnail">
                <img src="../../../assets/youtubeLogo.png" class="notThumbnailLogo"/>
              </div>
            </template>
          </div>
        </template>
        <template v-else>
          <div class="d-flex justify-content-between ma-3">
            {{ languageStore.get('plenarySession') }}
            <div>
              <v-btn prepend-icon="mdi-arrow-right" size="small" @click="clickedVideoConference">
                {{ languageStore.get('videoMinutes') }}
              </v-btn>
            </div>
          </div>
          <div class="backLogoArea">
            <logo-component
                class="backLogo"
            ></logo-component>
          </div>
        </template>
      </div>
      <div class="committees-wrapper">
        <div v-if="canScrollUp" class="scroll-btn scroll-up" @click="scrollCommittees('up')">
          <v-icon>mdi-chevron-up</v-icon>
        </div>
        <div class="committees" ref="committeesRef" @scroll="checkScroll">
          <template v-if="committee.length > 0 || computedCodes.length > 0">
            <template v-for="live in committee" :key="live.meetingId">
              <div class="committee" @click="clickVideo(live.liveId)">
                <div class="d-flex justify-content-between liveVideo">
                  <div class="title">{{ live.committeeId.codeNm }}</div>
                  <div class="d-flex">
                    <div><img src="../../../assets/youtubeLogo.png" class="ylogo"/></div>
                    <div class="liveText">live</div>
                  </div>
                </div>
                <div class="thumbnail">
                  <v-img :src="live.thumbnailUrl"></v-img>
                </div>
              </div>
            </template>
            <template v-for="code in computedCodes" :key="code.codeId">
              <div class="committee">
                <div class="d-flex justify-content-between ma-3">
                  <div>{{ code.codeNm }}</div>
                  <div>
                    <v-btn prepend-icon="mdi-arrow-right" size="small" @click="clickedVideoConference">
                      {{ languageStore.get('videoMinutes') }}
                    </v-btn>
                  </div>
                </div>
                <div class="backLogoArea">
                  <logo-component
                      class="backLogo"
                  ></logo-component>
                </div>
              </div>
            </template>
          </template>
          <div v-else class="committees-empty">
            <v-icon size="48" color="rgba(255,255,255,0.3)">mdi-video-off-outline</v-icon>
            <p>{{ languageStore.get('noCommitteeLive') }}</p>
          </div>
        </div>
        <div v-if="canScrollDown" class="scroll-btn scroll-down" @click="scrollCommittees('down')">
          <v-icon>mdi-chevron-down</v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.liveVideoArea {
  width: 90%;
  height: 65vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #052C65;
}

.liveVideos {
  display: grid;
  grid-template-columns: repeat(2, 38vw);
  grid-template-rows: repeat(2, 30vh);
  grid-gap: 10px;
  grid-template-areas:
        "ml005 committee"
        "ml005 committee";

}

.committees-wrapper {
  grid-area: committee;
  position: relative;
}

.committees {
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(2, 18.5vw);
  overflow-y: auto;
  height: 100%;
}

.committees::-webkit-scrollbar {
  width: 4px;
}

.committees::-webkit-scrollbar-thumb {
  background-color: rgba(255,255,255,0.3);
  border-radius: 2px;
}

.scroll-btn {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.9);
  color: #052C65;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.scroll-btn:hover {
  background-color: #ffffff;
  transform: translateX(-50%) scale(1.1);
}

.scroll-up {
  top: 4px;
}

.scroll-down {
  bottom: 4px;
}

.committee {
  width: 18.5vw;
  height: 30vh;
  background-color: #ffffff;
}

.ml005 {
  grid-area: ml005;
  background-color: #ffffff;
  padding: 10px;
  overflow: hidden;
}

.thumbnail {
  height: 85%;
  overflow: hidden;
  cursor: pointer;
}

.thumbnail :deep(.v-img) {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.liveVideo {
  cursor: pointer;
  font-size: 1.16rem;
  height: 10%;
  margin: 0 10px 5px 10px;
}

.liveText {
  font-weight: bold;
  color: red;
}

.backLogo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70%;
  height: 70%;
}

.backLogoArea {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80%;
  z-index: -1;
  opacity: 30%;
}

.title {
  font-weight: bold;
}

.ylogo {
  height: 30px;
  width: 30px;
}

.notThumbnail {
  background-color: #09223F;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  opacity: 80%;
}

.notThumbnail:hover {
  opacity: 100%;
}

.notThumbnailLogo {
  height: 25%;
  cursor: pointer;
}

.committees-empty {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  gap: 12px;
}

.loadingArea {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
</style>